import PendingPaymentBanner from '@/components/PendingPaymentBanner';
import PSFetchError from '@/components/PSFetchError';
import {
  PSHeaderBackButton,
  PSHeaderSettingsButton,
} from '@/components/PSHeaderButtons';
import ReservationListItem from '@/components/TicketListItem';
import TicketListItemSkeleton from '@/components/TicketListItemSkeleton';
import PageScrollView from '@/components/ui/PageScrollView';
import PSLinkButton from '@/components/ui/PSLinkButton';
import PSText from '@/components/ui/PSText';
import UserDetails from '@/components/UserDetails';
import MembershipCard from '@/components/MembershipCard';
import {
  useFetchParticipationRequests,
  type FetchParticipationRequestsState,
} from '@/src/hooks/useFetchParticipationRequests';
import {
  useFetchReservations,
  type FetchReservationsState,
} from '@/src/hooks/useFetchReservations';
import { restoreSession } from '@/src/hooks/useRestoredSession';
import { useSessionUser } from '@/src/lib/sessionStore';
import { Image } from 'expo-image';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import Head from 'expo-router/head';
import { useCallback, useEffect, useRef, useState, type Ref } from 'react';
import { View as RNView, type ScrollView as RNScrollView } from 'react-native';
import { getTokenValue, Text, XStack, YStack } from 'tamagui';
import { isBefore } from 'date-fns';
import { StatusBar } from 'expo-status-bar';
import Footer from '@/components/Footer';
import { usePendingMembership } from '@/src/hooks/usePendingMembership';
import APProfilePageUpsellBanner from '@/components/APProfilePageUpsellBanner';
import { space } from '@/constants/tokens';

export default function Profile() {
  const bookings = useFetchReservations();
  const participationRequests = useFetchParticipationRequests();
  // Pulled off the state because that object is rebuilt every render (see the
  // hook) while `refresh` itself is stable — so the handler below is too.
  const pendingMembership = usePendingMembership();
  const { refresh: refreshReservations } = bookings;
  const { refresh: refreshParticipationRequests } = participationRequests;
  const router = useRouter();
  const user = useSessionUser();
  const [isRefreshing, setIsRefreshing] = useState(false);

  // Jumping straight to ONE booking's row: any screen can send a reader here
  // with `router.push('/profile?bookingId=<id>')` (the event page's booked
  // banner does, when the event holds more than one booking and so has no
  // single ticket to open). The row's offset is MEASURED against the scroll
  // view's content node (`measureLayout`) rather than summed out of `onLayout`
  // y's — the row sits four stacks deep, and its OWN `onLayout` only re-fires
  // when its frame relative to its immediate parent changes, which a block
  // loading somewhere above `ReservationsSection` never does. So the chase is
  // driven off the two list fetches instead: once both have left `loading`,
  // every block above the row has taken its final height, and the measure
  // below is trustworthy — see the effect after `scrollToBooking`.
  const scrollRef = useRef<RNScrollView>(null);
  const targetRef = useRef<RNView>(null);
  const { bookingId: bookingIdToScrollTo } = useLocalSearchParams<{
    bookingId?: string;
  }>();
  const [isChasingBooking, setIsChasingBooking] =
    useState(!!bookingIdToScrollTo);

  const scrollToBooking = useCallback(() => {
    const scroll = scrollRef.current;
    const target = targetRef.current;
    if (!scroll || !target) return;
    // `getInnerViewRef`, not the typed `getInnerViewNode`: the latter hands
    // back a node handle, which Fabric's `measureLayout` refuses. RN's types
    // declare neither the method nor the prop that replaces it on web, but
    // both RN and RN-web expose it.
    const content = (
      scroll as unknown as { getInnerViewRef(): RNView | null }
    ).getInnerViewRef();
    if (!content) return;
    // Land the row a gutter below the viewport's top edge (the header is
    // opaque here, so that edge already clears it) rather than flush against
    // it, which reads as the row being cut off by the bar.
    target.measureLayout(content, (_x, y) => {
      scroll.scrollTo({ y: Math.max(y - space.lg, 0), animated: true });
      // Ends the chase itself, on every platform (RN-web never fires
      // `onScrollBeginDrag`, so that's not a backstop here) — otherwise a
      // later pull-to-refresh, with `bookingId` still in the URL, would yank
      // a reader back to a row they've since scrolled away from.
      setIsChasingBooking(false);
    });
  }, []);

  // Both list fetches gate the chase: `ApprovalPendingSection` and
  // `ReservationsSection` each swap between a skeleton/nothing and their real
  // content on `loading` → settled, and that's every layout change the row
  // can still be waiting on. Firing once per settle (not on every render)
  // is what lets `scrollToBooking` measure a row that's done moving.
  useEffect(() => {
    if (
      !isChasingBooking ||
      bookings.status === 'loading' ||
      participationRequests.status === 'loading'
    )
      return;
    scrollToBooking();
  }, [
    isChasingBooking,
    bookings.status,
    participationRequests.status,
    scrollToBooking,
  ]);

  // Both halves of the page, since both are stale for the same reasons: a
  // membership bought or a detail edited elsewhere lives on the user, the list
  // lives on the endpoint.
  const onRefresh = useCallback(async () => {
    setIsRefreshing(true);
    // allSettled, not all: the spinner has to stop even when a leg fails, and
    // each already reports its own failure — the list through its errored
    // section, a dead token by ending the session and popping this route.
    await Promise.allSettled([
      refreshReservations(),
      refreshParticipationRequests(),
      restoreSession(),
    ]);
    setIsRefreshing(false);
  }, [refreshReservations, refreshParticipationRequests]);

  if (!user) return null;
  return (
    <>
      <Head>
        <title>Profile · Putting Scene</title>
      </Head>
      <Stack.Screen
        options={{
          headerTransparent: false,
          headerTitle: () => (
            <Text
              fontSize={'$large'}
              fontWeight={'$semibold'}
              textAlign="center"
            >
              Profile
            </Text>
          ),
          headerStyle: {
            backgroundColor: getTokenValue('$background'),
          },

          // headerBlurEffect: 'extraLight',
          // headerLargeTitleEnabled: true,
          headerLeft: () => <PSHeaderBackButton />,
          headerRight: () => (
            <PSHeaderSettingsButton onPress={() => router.push('/settings')} />
          ),
          unstable_headerLeftItems: () => [
            {
              type: 'custom',
              element: <PSHeaderBackButton />,
              hidesSharedBackground: true,
            },
          ],
          unstable_headerRightItems: () => [
            {
              type: 'custom',
              element: (
                <PSHeaderSettingsButton
                  onPress={() => router.push('/settings')}
                />
              ),
              hidesSharedBackground: true,
            },
          ],
        }}
      />

      <StatusBar style="dark" />

      <PageScrollView
        ref={scrollRef}
        headerTransparent={false}
        isRefreshing={isRefreshing}
        onRefresh={onRefresh}
        onScrollBeginDrag={() => setIsChasingBooking(false)}
      >
        <YStack gap={'$space.xxl'}>
          {/* Above the contact details on purpose: what they hold is the fact
              they came here for. Both the heading and the card are dropped when
              there's no membership — an empty "Your Membership" section would
              read as a failed fetch, and home already sells the pass. */}

          <YStack gap={'$space.lg'}>
            <XStack justifyContent="space-between">
              <PSText.H2>Your Details</PSText.H2>
              <PSLinkButton
                title="Edit"
                onPress={() => router.push('/profile/edit')}
                variant="neutral"
              />
            </XStack>
            <YStack gap={'$space.sm'}>
              {/* The card draws an ENTITLEMENT, and a purchase still being
                  confirmed isn't one — it's on `pendingMembership`, a different
                  field with no plan or dates on it. So the two take the same
                  slot rather than the card learning a state it has no data for.
                  `/me` never sets both, and the banner draws nothing when
                  there's nothing pending, so this is exclusive either way. */}
              {user.membership && (
                <MembershipCard
                  membership={user.membership}
                  onPress={() =>
                    router.push(`/my-memberships/${user.membership?.id}`)
                  }
                />
              )}
              {pendingMembership && (
                <PendingPaymentBanner
                  pendingMembership={pendingMembership}
                  onCtaClick={() =>
                    router.push(`/my-memberships/${pendingMembership.id}`)
                  }
                />
              )}

              <UserDetails
                name={user.fullName}
                email={user.email}
                mobile={user.mobile}
                socialMediaLink={user.socialMediaLink || undefined}
                isOnlyEmailVerificationPending={
                  user.isOnlyEmailVerificationPending
                }
                isEmailMissing={user.isEmailMissing}
                areDetailsMissing={user.areDetailsMissing}
              />
              {!user.membership && !pendingMembership && (
                <APProfilePageUpsellBanner
                  onPress={() => router.push('/memberships')}
                />
              )}
            </YStack>
          </YStack>

          <ApprovalPendingSection
            participationRequestsFetchState={participationRequests}
          />

          <ReservationsSection
            reservationsFetchState={bookings}
            hasPendingParticipationRequests={hasPendingParticipationRequests(
              participationRequests
            )}
            targetBookingId={bookingIdToScrollTo}
            targetRef={targetRef}
          />
        </YStack>
        <Footer />
      </PageScrollView>
    </>
  );
}

/**
 * "Approval pending bookings" — applications still awaiting an organizer's
 * decision on Backstage. Unlike `ReservationsSection` below, this one has no
 * error or empty art of its own: it's a secondary section, so a failed fetch
 * or a reader with nothing pending just means the section isn't there, same
 * as the membership card's empty case above.
 */
/**
 * Whether the reader has an application still awaiting an organizer's
 * decision — the one participation-request fact `ReservationsSection` needs,
 * so it doesn't have to take the whole fetch state just to re-run this same
 * filter (see `ApprovalPendingSection`, which needs the full list instead).
 */
function hasPendingParticipationRequests(
  state: FetchParticipationRequestsState
): boolean {
  return (
    state.status === 'success' &&
    state.requests.some((r) => r.status === 'pending')
  );
}

function ApprovalPendingSection({
  participationRequestsFetchState,
}: {
  participationRequestsFetchState: FetchParticipationRequestsState;
}) {
  const router = useRouter();

  if (participationRequestsFetchState.status !== 'success') return null;

  const pending = participationRequestsFetchState.requests.filter(
    (r) => r.status === 'pending'
  );
  if (pending.length === 0) return null;

  return (
    <YStack gap="$space.lg">
      <PSText.H2>Bookings pending approval</PSText.H2>
      <YStack alignSelf="stretch" gap="$space.md">
        {pending.map((r) => (
          <ReservationListItem
            key={r.id}
            imageUrl={r.booking.event.images.at(0)?.url}
            title={r.booking.event.title}
            slotDatetime={r.booking.slot.startDatetime}
            ticketCount={r.booking.tickets.reduce(
              (sum, t) => sum + t.quantity,
              0
            )}
            venueName={r.booking.event.venue.name}
            googleMapsUrl={r.booking.event.venue.googleMapsUrl}
            onPress={() => router.push(`/requests/${r.id}`)}
          />
        ))}
      </YStack>
    </YStack>
  );
}

/**
 * The Tickets section body, branched on the (async) bookings fetch. Unlike the
 * event screen — where loading/error replace the WHOLE page — only this section
 * is behind the fetch here; "Your Details" above always renders, since the page
 * always has a user. Four states off `useFetchBookings`:
 *
 *   loading — skeleton rows (`TicketListItemSkeleton`, mirroring the real rows so
 *             they slot in without a jump)
 *   error   — `PSFetchError`: the cat, the failure's own words, and Retry
 *   empty   — a bare one-line placeholder, still to be designed
 *   success — an "Upcoming bookings" section followed by a "Past bookings" one,
 *             either dropped if empty (see the split below)
 *
 * Only the SECTION is replaced in the error case, never the page, which is what
 * makes `retry` worth having here: the reader keeps their details above and
 * re-asks in place, rather than being bounced through a full-page error the way
 * the event screen does it.
 *
 * The empty state is suppressed while a participation request is still
 * pending — "you have not put any scenes yet" directly under
 * `ApprovalPendingSection`'s own list would read as contradicting it, even
 * though zero of those have become a real booking yet.
 */
function ReservationsSection({
  reservationsFetchState,
  hasPendingParticipationRequests,
  targetBookingId,
  targetRef,
}: {
  reservationsFetchState: FetchReservationsState;
  hasPendingParticipationRequests: boolean;
  /** The one row the page wants scrolled to, if it was sent one. */
  targetBookingId?: string;
  targetRef: Ref<RNView>;
}) {
  const router = useRouter();

  if (reservationsFetchState.status === 'loading') {
    return <TicketListItemSkeleton />;
  }

  if (reservationsFetchState.status === 'error') {
    return (
      <PSFetchError
        message={reservationsFetchState.error.message}
        onRetry={reservationsFetchState.retry}
      />
    );
  }

  // Now reachable, where the dummy data made it impossible: a real account with
  // nothing booked. Without this the heading sits above blank space, which reads
  // as a failed render rather than an empty list.
  if (
    reservationsFetchState.reservations.length === 0 &&
    !hasPendingParticipationRequests
  ) {
    return <NoReservationsScreen />;
  }

  // The endpoint returns bookings newest-slot-first, so upcoming ones (sorted
  // soonest-first among themselves) all land before past ones — a single pass
  // splits them without re-sorting either half.
  const now = new Date();
  const upcoming: typeof reservationsFetchState.reservations = [];
  const past: typeof reservationsFetchState.reservations = [];
  for (const b of reservationsFetchState.reservations) {
    (isBefore(new Date(b.slot.endDatetime), now) ? past : upcoming).push(b);
  }

  return (
    <YStack gap="$space.xxl">
      {upcoming.length > 0 && (
        <YStack gap="$space.lg">
          <PSText.H2>Upcoming bookings</PSText.H2>
          <YStack alignSelf="stretch" gap="$md">
            {upcoming.map((b) => (
              // The wrapper is what the page measures and scrolls to (the row
              // forwards no ref) — on every row, not just the target, so the
              // list's element types don't change under it.
              <RNView
                key={b.id}
                ref={b.id === targetBookingId ? targetRef : undefined}
              >
                <ReservationListItem
                  imageUrl={b.event.images.at(0)?.url}
                  title={b.event.title}
                  slotDatetime={b.slot.startDatetime}
                  ticketCount={b.tickets.reduce(
                    (sum, t) => sum + t.quantity,
                    0
                  )}
                  venueName={b.event.venue.name}
                  googleMapsUrl={b.event.venue.googleMapsUrl}
                  onPress={() => router.push(`/bookings/${b.id}`)}
                />
              </RNView>
            ))}
          </YStack>
        </YStack>
      )}

      {past.length > 0 && (
        <YStack gap="$space.lg">
          <PSText.H2>Past bookings</PSText.H2>
          <YStack alignSelf="stretch" gap="$md">
            {past.map((b) => (
              <RNView
                key={b.id}
                ref={b.id === targetBookingId ? targetRef : undefined}
              >
                <ReservationListItem
                  imageUrl={b.event.images.at(0)?.url}
                  title={b.event.title}
                  slotDatetime={b.slot.startDatetime}
                  ticketCount={b.tickets.reduce(
                    (sum, t) => sum + t.quantity,
                    0
                  )}
                  venueName={b.event.venue.name}
                  googleMapsUrl={b.event.venue.googleMapsUrl}
                  onPress={() => router.push(`/bookings/${b.id}`)}
                  past
                />
              </RNView>
            ))}
          </YStack>
        </YStack>
      )}
    </YStack>
  );
}

/**
 * A bare one-line message for the tickets section — the stand-in until a state is
 * designed. Deliberately plain so it reads as a placeholder. The EMPTY list is all
 * that's left on it now that the errored state has art of its own.
 */
function NoReservationsScreen() {
  const router = useRouter();
  return (
    <YStack gap={'$space.xl'}>
      <PSText.H2>Your bookings</PSText.H2>
      <YStack alignItems="center" gap={'$space.sm'}>
        <Image
          source={require('@/assets/images/not-found-cat.png')}
          style={{ width: '100%', height: 200 }}
        />
        <Text fontSize="$base" color="$textDark">
          {`You have not put any scenes yet.`}
        </Text>
        <PSLinkButton
          title="Rectify that"
          onPress={() =>
            router.canGoBack() ? router.back() : router.push('/')
          }
        />
      </YStack>
    </YStack>
  );
}
